import { Icon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ValidateAudioCopy } from './validate-audio-copy';

/** Rejection reasons sent as is to the API (unchanged codes). */
export const REJECTION_REASONS = [
  ['audio_quality_poor', 'audioQualityPoor'],
  ['incorrect_pronunciation', 'incorrectPronunciation'],
  ['background_noise', 'backgroundNoise'],
  ['does_not_match_text', 'doesNotMatchText'],
  ['other', 'other'],
] as const;

type ValidateAudioDecisionPanelProps = {
  t: Record<string, unknown>;
  copy: ValidateAudioCopy;
  audioGender: string;
  onAudioGenderChange: (value: string) => void;
  rejectionReason: string;
  onRejectionReasonChange: (value: string) => void;
  detailedRejectionReason: string;
  onDetailedRejectionReasonChange: (value: string) => void;
  onReRecord: () => void;
};

/**
 * What the validator fills before deciding: the voice heard (required to
 * accept) and, to reject, a reason with optional details. Accept and reject
 * themselves sit in the action bar.
 */
export function ValidateAudioDecisionPanel({
  t,
  copy,
  audioGender,
  onAudioGenderChange,
  rejectionReason,
  onRejectionReasonChange,
  detailedRejectionReason,
  onDetailedRejectionReasonChange,
  onReRecord,
}: ValidateAudioDecisionPanelProps) {
  const s = (key: string) => String(t[key] ?? '');
  return (
    <section className="space-y-5 rounded-xl border border-border/60 bg-surface p-5 sm:p-6" aria-label={copy.decisionTitle}>
      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium text-foreground">
          {copy.voice} <span className="text-danger">*</span>
        </legend>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={copy.voice}>
          {(['female', 'male'] as const).map((value) => {
            const active = audioGender === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onAudioGenderChange(value)}
                className={cn(
                  'h-11 rounded-lg border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active ? 'border-primary bg-primary text-primary-foreground' : 'border-border/60 bg-surface-2 text-foreground hover:border-brand/40',
                )}
              >
                {value === 'female' ? copy.female : copy.male}
              </button>
            );
          })}
        </div>
        {!audioGender && <p className="text-xs text-faint-foreground">{s('genderRequiredError')}</p>}
      </fieldset>

      <div className="space-y-2">
        <label htmlFor="validate-audio-reason" className="text-sm font-medium text-foreground">
          {s('rejectionReason')}
        </label>
        <Select value={rejectionReason} onValueChange={onRejectionReasonChange}>
          <SelectTrigger id="validate-audio-reason" className="h-11 bg-surface">
            <SelectValue placeholder={s('rejectionReason')} />
          </SelectTrigger>
          <SelectContent>
            {REJECTION_REASONS.map(([value, key]) => (
              <SelectItem key={value} value={value}>
                {s(key)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="block space-y-1.5 pt-1">
          <span className="text-xs text-muted-foreground">{s('rejectionReasonOptional')}</span>
          <Textarea
            value={detailedRejectionReason}
            onChange={(e) => onDetailedRejectionReasonChange(e.target.value)}
            placeholder={s('rejectionReasonDetailedPlaceholder')}
            className="min-h-[80px]"
          />
        </label>
      </div>

      <button
        type="button"
        onClick={onReRecord}
        className="inline-flex h-11 items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <Icon name="mic" className="size-4" />
        {s('rejectAndReRecord')}
      </button>
    </section>
  );
}
